import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/components/AppShell";
import { useRealtime } from "@/lib/realtime";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import IssueCard from "@/components/IssueCard";
import Roadmap from "@/pages/Roadmap";
import Boards from "@/pages/Boards";
import SprintsTab from "@/pages/SprintsTab";
import PagesView from "@/pages/Pages";
import Releases from "@/pages/Releases";
import { ISSUE_TYPES, PRIORITIES, STATUSES, typeMeta, prioMeta, initials } from "@/lib/meta";
import { Plus, ChevronRight, ChevronDown, Play, CheckSquare, MoreHorizontal, Trash2, Pencil, Settings } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "board", label: "Board" },
  { id: "backlog", label: "Backlog" },
  { id: "sprints", label: "Sprints" },
  { id: "roadmap", label: "Roadmap" },
  { id: "releases", label: "Releases" },
  { id: "pages", label: "Pages" },
  { id: "issues", label: "Issues" },
];

export default function ProjectView() {
  const { projectId, tab = "overview" } = useParams();
  const nav = useNavigate();
  const { openIssue, users, projects, refreshRefs } = useApp();
  const [project, setProject] = useState(null);
  const [issues, setIssues] = useState([]);
  const [sprints, setSprints] = useState([]);

  // Edit project state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", key: "", description: "" });
  const [savingProject, setSavingProject] = useState(false);

  const load = useCallback(async () => {
    const [p, i, s] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/issues`, { params: { project_id: projectId } }),
      api.get(`/sprints`, { params: { project_id: projectId } }),
    ]);
    setProject(p.data); setIssues(i.data); setSprints(s.data);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("ft:issues-changed", h);
    return () => window.removeEventListener("ft:issues-changed", h);
  }, [load]);
  useRealtime((ev) => {
    if (!ev || !ev.project_id) return;
    if (ev.project_id !== projectId) return;
    if (["issue.created","issue.updated","issue.deleted","sprint.created","sprint.updated","sprint.completed","board.updated","project.updated"].includes(ev.type)) {
      load();
    }
  }, [projectId]);

  const handleOpenEdit = () => {
    setEditForm({
      name: project?.name || "",
      key: project?.key || "",
      description: project?.description || "",
    });
    setEditOpen(true);
  };

  const handleSaveProject = async () => {
    if (!editForm.name.trim()) {
      toast.error("Project name cannot be empty");
      return;
    }
    if (!editForm.key.trim()) {
      toast.error("Project short name (key) cannot be empty");
      return;
    }

    setSavingProject(true);
    try {
      const res = await api.patch(`/projects/${projectId}`, {
        name: editForm.name.trim(),
        key: editForm.key.trim().toUpperCase(),
        description: editForm.description.trim(),
      });
      toast.success(
        project?.key !== res.data.key
          ? `Project updated! All ticket IDs migrated to ${res.data.key}-*`
          : "Project updated successfully"
      );
      setEditOpen(false);
      load();
      refreshRefs?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update project");
    } finally {
      setSavingProject(false);
    }
  };

  if (!project) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const displayInitials = ((project.key || project.name || "PR").trim().slice(0, 2)).toUpperCase();

  return (
    <div className="flex flex-col h-full">
      <div className="px-6 pt-5 pb-2 border-b border-border shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 grid place-items-center rounded-md bg-primary/15 text-primary font-mono font-semibold">
              {displayInitials}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                <span>Project</span>
                <span>·</span>
                {project.key ? (
                  <span className="font-mono font-bold text-primary">{project.key}</span>
                ) : (
                  <span
                    className="text-amber-500 font-sans italic text-[11px] cursor-pointer hover:underline"
                    onClick={handleOpenEdit}
                    title="Click to assign short name"
                  >
                    (No short name set — Click to set)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <h1 className="font-display text-2xl font-semibold tracking-tight">{project.name}</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="w-7 h-7 text-muted-foreground hover:text-foreground"
                  onClick={handleOpenEdit}
                  title="Edit Project Name & Key"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleOpenEdit} className="gap-1.5 text-xs">
            <Pencil className="w-3.5 h-3.5" /> Edit Project
          </Button>
        </div>
        <div className="flex items-center gap-1">
          {TABS.map(t => (
            <button key={t.id} onClick={()=>nav(`/projects/${projectId}/${t.id}`)}
              data-testid={`proj-tab-${t.id}`}
              className={cn(
                "px-3 py-1.5 text-sm rounded-md transition-colors",
                tab === t.id ? "bg-secondary text-foreground font-medium" : "text-muted-foreground hover:text-foreground hover:bg-accent"
              )}>
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {tab === "overview" && <Overview project={project} issues={issues} sprints={sprints} users={users} />}
        {tab === "board" && <Boards project={project} />}
        {tab === "backlog" && <Backlog project={project} issues={issues} sprints={sprints} users={users} reload={load} openIssue={openIssue} />}
        {tab === "sprints" && <SprintsTab project={project} sprints={sprints} issues={issues} reload={load} />}
        {tab === "roadmap" && <Roadmap projectId={project.id} />}
        {tab === "releases" && <Releases project={project} />}
        {tab === "pages" && <PagesView scopedProjectId={project.id} />}
        {tab === "issues" && <IssuesTable project={project} issues={issues} users={users} openIssue={openIssue} />}
      </div>

      {/* Edit Project Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Pencil className="w-4 h-4 text-primary" /> Edit Project
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3.5 py-1">
            <div className="space-y-1.5">
              <Label>Project Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="e.g. Surana SFCC"
                data-testid="edit-proj-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Short Name / Key (Prefix for Tickets)</Label>
              <Input
                value={editForm.key}
                onChange={(e) => setEditForm({ ...editForm, key: e.target.value.toUpperCase() })}
                placeholder="e.g. SJ"
                maxLength={8}
                data-testid="edit-proj-key"
              />
              <div className="p-2.5 rounded-md bg-primary/10 border border-primary/20 text-xs text-primary leading-relaxed">
                💡 <strong>Automatic Ticket Migration:</strong> Setting or changing the short name to <strong>{editForm.key || "KEY"}</strong> will automatically update all existing tickets in this project (e.g. <code>-44</code> or <code>OLD-44</code> &rarr; <code>{editForm.key || "KEY"}-44</code>).
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={3}
                placeholder="Project description…"
                data-testid="edit-proj-desc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveProject} disabled={savingProject} data-testid="edit-proj-submit">
              {savingProject ? "Updating tickets…" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Overview --------
function Overview({ project, issues, sprints, users }) {
  const activeSprint = sprints.find(s => s.state === "active");
  const sprintIssues = activeSprint ? issues.filter(i => i.sprint_id === activeSprint.id) : [];
  const doneCount = sprintIssues.filter(i => i.status === "done").length;
  const pts = sprintIssues.reduce((a,b)=>a+(b.story_points||0),0);
  const donePts = sprintIssues.filter(i=>i.status==="done").reduce((a,b)=>a+(b.story_points||0),0);
  const openBugs = issues.filter(i => i.issue_type === "bug" && i.status !== "done").length;
  return (
    <div className="p-6 space-y-4 max-w-[1200px]">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total issues</div><div className="font-display text-3xl font-semibold">{issues.length}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Open bugs</div><div className="font-display text-3xl font-semibold text-red-500">{openBugs}</div></Card>
        <Card className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Sprints</div><div className="font-display text-3xl font-semibold">{sprints.length}</div></Card>
      </div>
      {activeSprint && (
        <Card className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-xs font-mono uppercase text-emerald-500 border border-emerald-500/30 bg-emerald-500/10 rounded px-1.5 py-0.5">Active</div>
            <h3 className="font-display text-lg font-semibold">{activeSprint.name}</h3>
          </div>
          <div className="text-sm text-muted-foreground mb-4">{activeSprint.goal || "No goal set"}</div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Progress</div><div className="font-mono">{doneCount} / {sprintIssues.length}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Points</div><div className="font-mono">{donePts} / {pts}</div></div>
            <div><div className="text-xs uppercase tracking-widest text-muted-foreground">Ends</div><div className="font-mono text-xs">{activeSprint.end_date ? new Date(activeSprint.end_date).toLocaleDateString() : "—"}</div></div>
          </div>
        </Card>
      )}
      <Card className="p-5">
        <div className="flex items-center gap-2 mb-3"><h3 className="font-display text-lg font-semibold">Description</h3></div>
        <div className="text-sm text-muted-foreground">{project.description || "No description yet."}</div>
      </Card>
    </div>
  );
}

// -------- Board (Kanban) --------
function Board({ project, issues, sprints, users, reload, openIssue }) {
  const activeSprint = sprints.find(s => s.state === "active");
  const [sprintFilter, setSprintFilter] = useState(activeSprint?.id || "all");
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  useEffect(() => { if (activeSprint && sprintFilter === "all") setSprintFilter(activeSprint.id); }, [activeSprint?.id]);

  let scoped = issues;
  if (sprintFilter && sprintFilter !== "all") scoped = issues.filter(i => i.sprint_id === sprintFilter);

  const cols = project.statuses || STATUSES.map(s => ({ id: s.id, name: s.label }));
  const grouped = Object.fromEntries(cols.map(c => [c.id, scoped.filter(i => i.status === c.id && i.issue_type !== "epic")]));

  const onDrop = async (statusId) => {
    if (!dragId) return;
    setOverCol(null); const id = dragId; setDragId(null);
    const iss = issues.find(x => x.id === id);
    if (!iss || iss.status === statusId) return;
    await api.patch(`/issues/${id}`, { status: statusId });
    reload();
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <Select value={sprintFilter} onValueChange={setSprintFilter}>
          <SelectTrigger className="w-64 h-9" data-testid="board-sprint-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All issues</SelectItem>
            {sprints.map(s => <SelectItem key={s.id} value={s.id}>{s.name} · {s.state}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-xs text-muted-foreground">{scoped.length} issues</div>
      </div>
      <div className="grid gap-3 overflow-x-auto pb-4" style={{gridTemplateColumns:`repeat(${cols.length}, minmax(280px, 1fr))`}}>
        {cols.map(col => (
          <div key={col.id}
            onDragOver={(e)=>{ e.preventDefault(); setOverCol(col.id); }}
            onDragLeave={()=>setOverCol(null)}
            onDrop={()=>onDrop(col.id)}
            data-testid={`board-col-${col.id}`}
            className={cn("rounded-md bg-secondary/40 border border-border/50 p-2 flex flex-col min-h-[300px]", overCol === col.id && "dashed-drop")}>
            <div className="flex items-center justify-between px-2 py-1.5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-medium">
                <span>{col.name}</span>
                <span className="text-muted-foreground font-mono">{grouped[col.id]?.length || 0}</span>
              </div>
            </div>
            <div className="space-y-2 flex-1">
              {(grouped[col.id] || []).map(i => (
                <IssueCard key={i.id} issue={i} users={users}
                  onClick={()=>openIssue(i.id)}
                  draggable
                  onDragStart={()=>setDragId(i.id)}
                  onDragEnd={()=>setDragId(null)}
                  dragging={dragId === i.id} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- Backlog --------
function Backlog({ project, issues, sprints, users, reload, openIssue }) {
  const [dragId, setDragId] = useState(null);
  const [overSprint, setOverSprint] = useState(null);
  const backlog = issues.filter(i => !i.sprint_id && i.issue_type !== "epic");
  const activeAndFuture = sprints.filter(s => s.state !== "closed");

  const moveTo = async (sprintId) => {
    if (!dragId) return;
    const id = dragId; setDragId(null); setOverSprint(null);
    await api.patch(`/issues/${id}`, { sprint_id: sprintId });
    reload();
  };

  const startSprint = async (id) => {
    await api.patch(`/sprints/${id}`, { state: "active", start_date: new Date().toISOString() });
    toast.success("Sprint started"); reload();
  };
  const completeSprint = async (id) => {
    if (!window.confirm("Complete sprint? Incomplete issues will return to the backlog.")) return;
    await api.post(`/sprints/${id}/complete`);
    toast.success("Sprint completed"); reload();
  };

  return (
    <div className="p-6 space-y-5 max-w-[1400px]">
      {activeAndFuture.map(s => {
        const sIssues = issues.filter(i => i.sprint_id === s.id && i.issue_type !== "epic");
        const pts = sIssues.reduce((a,b)=>a+(b.story_points||0),0);
        return (
          <div key={s.id}
            onDragOver={(e)=>{ e.preventDefault(); setOverSprint(s.id); }}
            onDragLeave={()=>setOverSprint(null)}
            onDrop={()=>moveTo(s.id)}
            data-testid={`sprint-section-${s.id}`}
            className={cn("border border-border rounded-md", overSprint === s.id && "dashed-drop")}>
            <div className="px-4 py-2.5 border-b border-border flex items-center gap-3">
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">{s.name}</span>
              <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-mono uppercase",
                s.state==="active" ? "bg-emerald-500/10 text-emerald-500" : "bg-secondary text-muted-foreground")}>{s.state}</span>
              <span className="text-xs text-muted-foreground">{sIssues.length} issues · {pts} pts</span>
              <div className="ml-auto flex gap-2">
                {s.state === "future" && <Button size="sm" variant="outline" onClick={()=>startSprint(s.id)} data-testid={`start-sprint-${s.id}`}><Play className="w-3.5 h-3.5 mr-1" /> Start</Button>}
                {s.state === "active" && <Button size="sm" variant="outline" onClick={()=>completeSprint(s.id)} data-testid={`complete-sprint-${s.id}`}><CheckSquare className="w-3.5 h-3.5 mr-1" /> Complete</Button>}
              </div>
            </div>
            <div className="p-2 space-y-1 min-h-[60px]">
              {sIssues.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">Drag issues here</div>}
              {sIssues.map(i => (
                <BacklogRow key={i.id} issue={i} users={users} openIssue={openIssue}
                  onDragStart={()=>setDragId(i.id)} onDragEnd={()=>setDragId(null)} dragging={dragId===i.id} />
              ))}
            </div>
          </div>
        );
      })}
      <div
        onDragOver={(e)=>{ e.preventDefault(); setOverSprint("backlog"); }}
        onDragLeave={()=>setOverSprint(null)}
        onDrop={()=>moveTo(null)}
        data-testid="backlog-section"
        className={cn("border border-border rounded-md", overSprint === "backlog" && "dashed-drop")}>
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-3">
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium">Backlog</span>
          <span className="text-xs text-muted-foreground">{backlog.length} issues</span>
        </div>
        <div className="p-2 space-y-1 min-h-[60px]">
          {backlog.length === 0 && <div className="text-xs text-muted-foreground text-center py-4">No issues in backlog</div>}
          {backlog.map(i => (
            <BacklogRow key={i.id} issue={i} users={users} openIssue={openIssue}
              onDragStart={()=>setDragId(i.id)} onDragEnd={()=>setDragId(null)} dragging={dragId===i.id} />
          ))}
        </div>
      </div>
    </div>
  );
}
function BacklogRow({ issue, users, openIssue, onDragStart, onDragEnd, dragging }) {
  const T = typeMeta(issue.issue_type); const P = prioMeta(issue.priority);
  const assignee = users.find(u => u.id === issue.assignee_id);
  return (
    <div
      draggable onDragStart={onDragStart} onDragEnd={onDragEnd}
      onClick={()=>openIssue(issue.id)}
      data-testid={`backlog-row-${issue.key}`}
      className={cn("flex items-center gap-3 px-3 py-2 rounded hover:bg-accent cursor-pointer transition-colors border border-transparent hover:border-border", dragging && "opacity-40")}>
      <T.icon className={cn("w-4 h-4 shrink-0", T.cls)} />
      <span className="font-mono text-xs text-muted-foreground w-16 shrink-0">{issue.key}</span>
      <span className="flex-1 truncate text-sm">{issue.summary}</span>
      <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border status-${issue.status} shrink-0`}>{STATUSES.find(s=>s.id===issue.status)?.label}</span>
      <P.icon className={cn("w-4 h-4 shrink-0", P.cls)} />
      {issue.story_points != null && <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-secondary text-[10px] font-mono font-semibold">{issue.story_points}</span>}
      {assignee ? (
        <Avatar className="w-5 h-5"><AvatarImage src={assignee.avatar_url} /><AvatarFallback className="text-[8px]">{initials(assignee.name)}</AvatarFallback></Avatar>
      ) : <div className="w-5 h-5 rounded-full border border-dashed border-border" />}
    </div>
  );
}

// -------- Sprints tab (deprecated inline, kept for backwards-compat if referenced) --------
function _LegacySprintsInlineTab({ project, sprints, issues, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", goal: "" });
  const submit = async () => {
    await api.post("/sprints", { project_id: project.id, ...form });
    toast.success("Sprint created"); setOpen(false); setForm({ name: "", goal: "" }); reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete sprint?")) return;
    await api.delete(`/sprints/${id}`); reload();
  };
  return (
    <div className="p-6 max-w-[1200px]">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-semibold">Sprints</h2>
        <Button size="sm" onClick={()=>setOpen(true)} data-testid="new-sprint-btn"><Plus className="w-4 h-4 mr-1" /> New sprint</Button>
      </div>
      <div className="space-y-2">
        {sprints.map(s => {
          const sIssues = issues.filter(i => i.sprint_id === s.id);
          return (
            <Card key={s.id} className="p-4 flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-medium">{s.name}</div>
                  <span className={cn("text-[10px] px-1.5 py-0.5 rounded font-mono uppercase",
                    s.state==="active" ? "bg-emerald-500/10 text-emerald-500" :
                    s.state==="closed" ? "bg-slate-500/10 text-slate-500" : "bg-secondary text-muted-foreground")}>{s.state}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{s.goal || "No goal"} · {sIssues.length} issues</div>
              </div>
              <Button variant="ghost" size="icon" onClick={()=>del(s.id)} data-testid={`del-sprint-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
            </Card>
          );
        })}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New sprint</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="ns-name" /></div>
            <div><Label>Goal</Label><Textarea value={form.goal} onChange={(e)=>setForm({...form,goal:e.target.value})} rows={3} data-testid="ns-goal" /></div>
          </div>
          <DialogFooter><Button onClick={submit} data-testid="ns-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Issues table --------
function IssuesTable({ project, issues, users, openIssue }) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const filtered = issues.filter(i =>
    (q === "" || i.summary.toLowerCase().includes(q.toLowerCase()) || i.key.toLowerCase().includes(q.toLowerCase())) &&
    (type === "all" || i.issue_type === type) &&
    (status === "all" || i.status === status)
  );
  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Input placeholder="Filter issues…" value={q} onChange={(e)=>setQ(e.target.value)} className="max-w-xs h-9" data-testid="issues-filter" />
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-32 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {ISSUE_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto text-xs text-muted-foreground">{filtered.length} issues</div>
      </div>
      <div className="border border-border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-xs uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Key</th>
              <th className="text-left px-3 py-2 font-medium">Summary</th>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Priority</th>
              <th className="text-left px-3 py-2 font-medium">Assignee</th>
              <th className="text-left px-3 py-2 font-medium">Points</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(i => {
              const T = typeMeta(i.issue_type); const P = prioMeta(i.priority);
              const a = users.find(u => u.id === i.assignee_id);
              return (
                <tr key={i.id} onClick={()=>openIssue(i.id)} data-testid={`issues-row-${i.key}`} className="border-t border-border hover:bg-accent cursor-pointer transition-colors">
                  <td className="px-3 py-2"><div className="flex items-center gap-2"><T.icon className={cn("w-4 h-4", T.cls)} /><span className="font-mono text-xs text-muted-foreground">{i.key}</span></div></td>
                  <td className="px-3 py-2 max-w-md truncate">{i.summary}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border status-${i.status}`}>{STATUSES.find(s=>s.id===i.status)?.label}</span></td>
                  <td className="px-3 py-2"><div className="flex items-center gap-1.5"><P.icon className={cn("w-4 h-4", P.cls)} /><span className="capitalize text-xs">{i.priority}</span></div></td>
                  <td className="px-3 py-2">{a ? <div className="flex items-center gap-1.5"><Avatar className="w-5 h-5"><AvatarImage src={a.avatar_url} /><AvatarFallback className="text-[8px]">{initials(a.name)}</AvatarFallback></Avatar><span className="text-xs">{a.name}</span></div> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2 font-mono text-xs">{i.story_points ?? "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">No issues match your filters.</div>}
      </div>
    </div>
  );
}
